import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  TextField, Button, IconButton, Chip, CircularProgress,
  Stack, Autocomplete, Tooltip, Alert, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getProjectSharing, shareProject, listOrgUsers } from "../utils/api";
import { useImpersonation } from "../contexts/ImpersonationContext";

/**
 * Modal for managing a project's sharing.
 *  - Lists current shared users + each instance's provisioning status
 *  - Autocomplete to add new users (resolves email → user_id from org)
 *  - "Share with entire org" toggle
 *  - Add/remove ops are async — modal stays open showing status
 */
export default function ShareModal({ open, onClose, projectId, orgId, onChanged }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Sharing is a WRITE. Under per-user view-as of a non-test org the server
  // rejects it (`read_only_view_as`), so say so up front instead of letting the
  // Share click fail — same guard AnalyzeChat/Data already apply to their writes.
  const { viewAsReadOnly, actingUser } = useImpersonation();

  // Initial load: fetch sharing state AND org users (once).
  // The API returns {error: <machine code>, message: <human sentence>}. Showing
  // `error` alone surfaced bare codes like "read_only_view_as" with no hint at
  // the cause or the fix, so prefer the sentence and keep the code as fallback.
  const errText = (e) =>
    e?.response?.data?.message || e?.response?.data?.error || e.message;

  const initialLoad = async () => {
    setLoading(true);
    try {
      const data = await getProjectSharing(projectId);
      setSharing(data);
      if (orgId) {
        try {
          const u = await listOrgUsers(orgId);
          setOrgUsers(u.users || []);
        } catch {
          setOrgUsers([]);
        }
      }
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  };

  // Polling refresh: ONLY fetch sharing state (not org users); slower cadence;
  // only when there are non-terminal instances to watch.
  const refreshSharing = async () => {
    try {
      const data = await getProjectSharing(projectId);
      setSharing(data);
    } catch (_) {
      // Silent on poll errors — don't spam the UI
    }
  };

  useEffect(() => {
    if (!open) return;
    setError(null);
    initialLoad();
  }, [open, projectId]); // eslint-disable-line

  // Only poll while at least one instance is provisioning/removing.
  useEffect(() => {
    if (!open || !sharing) return;
    const stillBusy = (sharing.shared_instances || []).some(
      (i) => i.status === "provisioning" || i.status === "removing"
    );
    if (!stillBusy) return;
    const t = setInterval(refreshSharing, 10000);   // 10s instead of 6s
    return () => clearInterval(t);
  }, [open, sharing]); // eslint-disable-line

  const handleAdd = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await shareProject(projectId, { add: [selected.user_id] });
      setSelected(null);
      await refreshSharing();
      onChanged?.();
    } catch (e) {
      setError(errText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (userId) => {
    setSubmitting(true);
    setError(null);
    try {
      await shareProject(projectId, { remove: [userId] });
      await refreshSharing();
      onChanged?.();
    } catch (e) {
      setError(errText(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Filter out already-shared users from the autocomplete
  const alreadyShared = new Set([
    ...(sharing?.shared_with || []),
    sharing?.owner_user_id,
  ]);
  const candidateUsers = orgUsers.filter((u) => !alreadyShared.has(u.user_id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Share deployment
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {viewAsReadOnly && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                Read-only: you're viewing as {actingUser?.email || "another user"}. Switch to
                "Act as myself" or the org-wide "Org Admin" entry to change sharing.
              </Alert>
            )}
            {sharing?.owner_user_id && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                  OWNER
                </Typography>
                <Typography variant="body2">
                  {sharing.owner_display_name || sharing.owner_email || sharing.owner_user_id}
                </Typography>
                {sharing.owner_email && sharing.owner_display_name && sharing.owner_email !== sharing.owner_display_name && (
                  <Typography variant="caption" color="text.secondary">
                    {sharing.owner_email}
                  </Typography>
                )}
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                ADD USER
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={candidateUsers}
                  getOptionLabel={(u) => `${u.display_name || u.email || u.user_id}`}
                  value={selected}
                  onChange={(_, v) => setSelected(v)}
                  renderInput={(params) => <TextField {...params} placeholder="Search users in org…" />}
                />
                <Tooltip
                  title={viewAsReadOnly ? "Read-only: you're viewing another user's data" : ""}
                  disableHoverListener={!viewAsReadOnly}
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAdd}
                      disabled={!selected || submitting || viewAsReadOnly}
                      sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none" }}
                    >
                      Share
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                SHARED USERS ({sharing?.shared_instances?.length || 0})
              </Typography>
              {(!sharing?.shared_instances?.length) ? (
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                  No shared users yet.
                </Typography>
              ) : (
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {sharing.shared_instances.map((inst) => {
                    const primary = inst.display_name || inst.email || inst.user_id;
                    const secondary = inst.email && inst.email !== primary ? inst.email : null;
                    return (
                      <Box
                        key={inst.user_id}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1,
                          p: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {primary}
                            {inst.is_admin && (
                              <Chip label="admin" size="small" color="primary" variant="outlined"
                                    sx={{ ml: 1, height: 18, fontSize: 10 }} />
                            )}
                          </Typography>
                          {secondary && (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {secondary}
                            </Typography>
                          )}
                        </Box>
                        <StatusChip status={inst.status} error={inst.provisioning_error} />
                        <IconButton size="small" onClick={() => handleRemove(inst.user_id)} disabled={submitting || viewAsReadOnly}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>

            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function StatusChip({ status, error }) {
  let color = "default";
  if (status === "ready") color = "success";
  else if (status === "provisioning") color = "info";
  else if (status === "removing") color = "warning";
  else if (status === "error") color = "error";
  return <Chip size="small" label={status} color={color} title={error || ""} sx={{ textTransform: "capitalize" }} />;
}
